import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const UserContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5250/api';

export function UserProvider({ children }) {
  const { user: authUser, isAuthenticated, getAuthHeader, isLoading: authLoading } = useAuth();

  const [profile, setProfile] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [escrowBalance, setEscrowBalance] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch user profile
  const fetchProfile = useCallback(async () => {
    if (!isAuthenticated) {
      setProfile(null);
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/profiles/me`, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 404) {
        // Profile doesn't exist yet, that's okay
        setProfile(null);
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const result = await response.json();
      const profileData = result.data || result;
      setProfile(profileData);
      return profileData;
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeader]);

  // Fetch wallet balance
  const fetchWallet = useCallback(async () => {
    if (!isAuthenticated) {
      setWalletBalance(null);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/wallets/me`, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const result = await response.json();
        const data = result.data || result;
        setWalletBalance(data.balance);
        setEscrowBalance(data.escrowBalance || 0);
      }
    } catch (err) {
      console.error('Failed to fetch wallet:', err);
    }
  }, [isAuthenticated, getAuthHeader]);

  // Refresh wallet balance
  const refreshWallet = useCallback(() => fetchWallet(), [fetchWallet]);

  // Update user profile
  const updateProfile = useCallback(async (profileData) => {
    if (!isAuthenticated) {
      return { success: false, error: 'Not authenticated' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/profiles/me`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || errorData.message || 'Failed to update profile');
      }

      const result = await response.json();
      const updatedProfile = result.data || result;
      setProfile(updatedProfile);
      return { success: true, profile: updatedProfile };
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeader]);

  // Update user mode
  const updateMode = useCallback(async (mode) => {
    if (!isAuthenticated) {
      return { success: false, error: 'Not authenticated' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/users/me/mode`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mode }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || errorData.message || 'Failed to update mode');
      }

      const result = await response.json();
      return { success: true, user: result.data || result };
    } catch (err) {
      console.error('Failed to update mode:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeader]);

  // Upload profile avatar
  const uploadAvatar = useCallback(async (file) => {
    if (!isAuthenticated) {
      return { success: false, error: 'Not authenticated' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/profiles/me/avatar`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || errorData.message || 'Failed to upload avatar');
      }

      const result = await response.json();
      const data = result.data || result;
      setProfile(prev => prev ? { ...prev, avatarUrl: data.avatarUrl } : null);
      return { success: true, avatarUrl: data.avatarUrl };
    } catch (err) {
      console.error('Failed to upload avatar:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeader]);

  // Delete user account
  const deleteAccount = useCallback(async () => {
    if (!isAuthenticated) {
      return { success: false, error: 'Not authenticated' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/users/me`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || errorData.message || 'Failed to delete account');
      }

      setProfile(null);
      return { success: true };
    } catch (err) {
      console.error('Failed to delete account:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAuthHeader]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Sync with auth state - fetch profile and wallet when authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchProfile();
      fetchWallet();
    } else if (!isAuthenticated) {
      setProfile(null);
      setWalletBalance(null);
      setEscrowBalance(null);
    }
  }, [isAuthenticated, authLoading, fetchProfile, fetchWallet]);

  // Computed values
  const currentUser = authUser ? {
    ...authUser,
    profile,
  } : null;

  const value = {
    currentUser,
    profile,
    walletBalance,
    escrowBalance,
    isLoading: isLoading || authLoading,
    error,
    fetchProfile,
    updateProfile,
    updateMode,
    uploadAvatar,
    deleteAccount,
    clearError,
    refreshWallet,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export default UserContext;
